import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Button from '../components/ui/Button';

const DynamicProduct = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const createSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products/`);
        const products = await response.json();
        
        const foundProduct = products.find(p => createSlug(p.title) === slug);
        
        if (foundProduct) {
          setProduct(foundProduct);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching product:', error);
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white lg:bg-cream">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white lg:bg-cream">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-text-primary mb-4">Product Not Found</h1>
            <p className="text-text-primary mb-8">The product you're looking for doesn't exist.</p>
            <Button variant="primary"><a href="/products">Back to Products</a></Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white lg:bg-cream">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-white lg:bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {product.image && (
              <div>
                <img 
                  src={product.image.startsWith('http') ? product.image : `${process.env.REACT_APP_API_URL}${product.image}`}
                  alt={product.title}
                  className="w-full h-96 object-cover rounded-2xl shadow-lg"
                />
              </div>
            )}
            
            <div>
              <div className="text-sm font-semibold text-primary mb-3 uppercase tracking-wide">
                Product Details
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-text-primary mb-6 leading-tight">
                {product.title}
              </h1>
              <p className="text-text-primary mb-6 text-lg leading-relaxed">
                {product.description}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button variant="primary"><a href="/contact">Contact Us</a></Button>
                <Button variant="secondary"><a href="#subcategories">View All Products</a></Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subcategories Section */}
      {product.subcategories && product.subcategories.length > 0 && (
        <section id="subcategories" className="py-16 bg-cream">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">Available Products</h2>
              <p className="text-text-primary max-w-3xl mx-auto">Explore our range of specialized products in this category</p>
            </div>
            <div className="bg-white rounded-3xl shadow-lg p-8 lg:p-12">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {product.subcategories.filter(sub => sub.is_active).map(subcategory => (
                  <li key={subcategory.id} className="flex items-start gap-3 p-4 rounded-xl hover:bg-cream transition-colors">
                    <svg className="w-6 h-6 text-primary flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <div>
                      <span className="font-semibold text-text-primary text-lg">{subcategory.name}</span>
                      {subcategory.description && subcategory.description !== '.' && (
                        <p className="text-brown-light mt-1">{subcategory.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default DynamicProduct;